import React, { useState } from 'react'
import { FileText, Share2, Download, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Eye, Lock, Globe, Instagram, Facebook, Linkedin, Youtube, MapPin, X } from 'lucide-react'
import { XIcon } from '../../icons/XIcon'

interface PDFPreviewContentProps {
  data: {
    title?: string
    description?: string
    pdf_url?: string
    bookCover?: string
    bookTitle?: string
    author?: string
    showDirectly?: boolean
    password?: string
    welcomeScreen?: {
      enabled: boolean
      image: string
      duration: number
    }
    socialLinks?: Record<string, string>
    companyPosition?: 'top' | 'bottom'
    buttonStyle?: {
      backgroundColor?: string
      textColor?: string
      borderRadius?: string
      shadow?: boolean
      text?: string
      shape?: string
    }
    qrCodeName?: string
    fonts?: {
      heading?: string
      body?: string
    }
    textSizes?: {
      branding?: string
      title?: string
      description?: string
    }
    openDirectly?: boolean
    animationTrigger?: number
  }
  branding: {
    companyName?: string
    brandingType?: 'beyondpages' | 'custom' | 'none'
    textColor?: string
    primaryColor?: string
    secondaryColor?: string
  }
  bgGradient?: string
  colorMode?: 'gradient' | 'structured'
  isPreview?: boolean // Add prop to distinguish preview mode from production
}

export const PDFPreviewContent: React.FC<PDFPreviewContentProps> = ({ 
  data, 
  branding,
  bgGradient = 'linear-gradient(to bottom right, #059669, #047857)',
  colorMode = 'gradient',
  isPreview = true // Default to preview mode for backward compatibility
}) => {
  const [showWelcomeScreen, setShowWelcomeScreen] = React.useState(false)
  const [showPDFViewer, setShowPDFViewer] = useState(false)

  // Debug logging only in development
  if (process.env.NODE_ENV === 'development') {
    console.log('PDFPreviewContent - data:', data)
    console.log('PDFPreviewContent - pdf_url:', data.pdf_url)
    console.log('PDFPreviewContent - showDirectly:', data.showDirectly)
    console.log('PDFPreviewContent - welcomeScreen:', data.welcomeScreen)
    console.log('PDFPreviewContent - branding:', branding)
    console.log('PDFPreviewContent - colorMode:', colorMode)
  }

  // Handle PDF download
  const handleDownload = () => {
    if (!data.pdf_url) return
    
    // Open PDF in new tab for download
    window.open(data.pdf_url, '_blank')
  }

  // Handle PDF preview
  const handlePreview = () => {
    if (!data.pdf_url) return
    // In production, open PDF directly in new tab for better mobile experience
    if (!isPreview) {
      window.open(data.pdf_url, '_blank')
    } else {
      // In preview mode, show the modal
      setShowPDFViewer(true)
    }
  }

  // Show/hide welcome screen based on enabled state
  React.useEffect(() => {
    // If welcome screen is disabled, always hide it
    if (!data.welcomeScreen?.enabled || !data.welcomeScreen?.image) {
      setShowWelcomeScreen(false)
    }
  }, [data.welcomeScreen?.enabled, data.welcomeScreen?.image])

  // Trigger animation when button is clicked
  React.useEffect(() => {
    if (data.animationTrigger && data.animationTrigger > 0 && data.welcomeScreen?.enabled && data.welcomeScreen?.image) {
      // Start the animation
      setShowWelcomeScreen(true)
      
      // Hide after duration
      const timer = setTimeout(() => {
        setShowWelcomeScreen(false)
      }, (data.welcomeScreen.duration || 3) * 1000)
      
      return () => {
        clearTimeout(timer)
      }
    }
  }, [data.animationTrigger])

  // Load Google Fonts if needed
  React.useEffect(() => {
    const fonts = new Set([data.fonts?.heading, data.fonts?.body].filter(Boolean))
    fonts.forEach(font => {
      if (font && font !== 'Inter') {
        const link = document.createElement('link')
        link.href = `https://fonts.googleapis.com/css2?family=${font.replace(' ', '+')}:wght@400;600;700&display=swap`
        link.rel = 'stylesheet'
        document.head.appendChild(link)
      }
    })
  }, [data.fonts])
  // Social media icons mapping
  const socialIcons: Record<string, any> = {
    website: Globe,
    instagram: Instagram,
    facebook: Facebook,
    twitter: XIcon,
    linkedin: Linkedin,
    youtube: Youtube,
    location: MapPin
  }

  // Use the user-selected text color consistently
  const textColor = branding.textColor || 'white'

  // If openDirectly is true, show PDF in full frame without any styling
  if (data.openDirectly === true && data.pdf_url) {
    return (
      <div className="h-full w-full bg-white overflow-hidden">
        {/* Safe area padding for mobile (56px), minimal for tablet (16px) */}
        <div className={`h-full w-full ${isPreview ? 'pt-14 sm:pt-4' : 'pt-0'}`}>
          <object
            data={`${data.pdf_url}#toolbar=0&navpanes=0&scrollbar=0&view=FitW`}
            type="application/pdf"
            className="w-full h-full"
          >
            <div className="flex items-center justify-center h-full p-4 bg-white">
              <p className="text-center text-gray-600">
                PDF kann nicht angezeigt werden.
                <a href={data.pdf_url} className="text-blue-600 underline ml-1">
                  Hier herunterladen
                </a>
              </p>
            </div>
          </object>
        </div>
      </div>
    )
  }

  // Show welcome screen if enabled
  if (showWelcomeScreen && data.welcomeScreen?.image) {
    return (
      <div className="h-full flex items-center justify-center bg-black">
        <img 
          src={data.welcomeScreen.image} 
          alt="Welcome" 
          className="max-w-full max-h-full object-contain"
          onClick={() => setShowWelcomeScreen(false)}
        />
      </div>
    )
  }

  return (
    <div className={`h-full flex flex-col bg-gray-50 ${isPreview ? 'pt-14 sm:pt-4' : 'pt-0'}`}>
      {/* Content */}
      <div className="flex-1 overflow-y-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {colorMode === 'gradient' ? (
          // Gradient mode - single continuous gradient
          <div className="min-h-full relative" style={{
            background: `linear-gradient(135deg, ${branding.primaryColor} 0%, ${branding.secondaryColor} 100%)`
          }}>
            {/* Content container with same structure as structured mode */}
            <div className="min-h-full flex flex-col">
              {/* Header section - 65% */}
              <div 
                className="px-6 relative"
                style={{
                  minHeight: '65%',
                  paddingTop: '0.5rem',
                  paddingBottom: '6rem' // Fixed padding for consistent PDF spacing
                }}
              >
                {/* Content wrapper with dynamic spacing */}
                <div className="h-full flex flex-col">
                  {/* Company name at top for custom branding (only when position is top) */}
                  {branding.brandingType === 'custom' && branding.companyName && data.companyPosition === 'top' && (
                    <div className="text-center mb-2">
                      <h2 className={`${data.textSizes?.branding || 'text-lg'} font-semibold`} style={{ color: branding.textColor }}>
                        {branding.companyName}
                      </h2>
                    </div>
                  )}

                  <div className="text-center">
                    {data.title && (
                      <h1 className={`${data.textSizes?.title || 'text-2xl'} font-bold mb-3 break-words`} style={{ 
                        color: branding.textColor,
                        fontFamily: data.fonts?.heading || 'Inter'
                      }}>
                        {data.title}
                      </h1>
                    )}
                    {data.description && (
                      <p className={`${data.textSizes?.description || 'text-base'} opacity-90 break-words whitespace-pre-wrap`} style={{ 
                        color: branding.textColor,
                        fontFamily: data.fonts?.body || 'Inter'
                      }}>
                        {data.description}
                      </p>
                    )}
                  </div>
                  
                  {/* Spacer that pushes content to top and maintains consistent distance to PDF */}
                  <div className="flex-1"></div>
                </div>
              </div>

              {/* Body section - no separate background */}
              <div className="flex-1 px-6 py-8">
              {/* PDF positioned to overlap both sections */}
              <div className="max-w-sm mx-auto relative" style={{ marginTop: '-7rem' }}>

                {/* Always show PDF preview if URL exists */}
                {data.pdf_url ? (
                  <>
                    <div className="w-full bg-white rounded-lg shadow-lg mb-4 overflow-hidden">
                      <div className="relative bg-gray-50" style={{ paddingBottom: '141.42%' /* A4 ratio 297/210 */ }}>
                        {/* PDF first page preview - non-scrollable */}
                        <div className="absolute inset-0 p-2 overflow-hidden">
                          <div className="w-full h-full relative bg-white rounded">
                            {isPreview ? (
                              // Preview mode - original behavior
                              <iframe
                                src={`${data.pdf_url}#page=1&toolbar=0&navpanes=0&scrollbar=0&statusbar=0&messages=0&scrolling=no&view=FitH`}
                                className="w-full h-full"
                                style={{ 
                                  border: 'none',
                                  pointerEvents: 'none',
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '100%',
                                  overflow: 'hidden'
                                }}
                                scrolling="no"
                                title="PDF Vorschau - Erste Seite"
                              />
                            ) : (
                              // Production mode - use object element
                              <object
                                data={`${data.pdf_url}#page=1&toolbar=0&navpanes=0&view=FitV`}
                                type="application/pdf"
                                className="w-full h-full"
                                style={{ 
                                  border: 'none',
                                  pointerEvents: 'none'
                                }}
                                title="PDF Vorschau - Erste Seite"
                              >
                                <p className="p-4 text-center text-gray-500">PDF Preview</p>
                              </object>
                            )}
                          </div>
                        </div>
                        {/* Overlay to ensure no interaction */}
                        <div className="absolute inset-0" />
                      </div>
                    </div>
                    
                    {/* Always show preview button when PDF exists */}
                    <button
                      onClick={handlePreview}
                      className={`w-full py-3 px-6 font-medium transform transition-transform hover:scale-105 flex items-center justify-center gap-2 ${
                        data.buttonStyle?.shadow ? 'shadow-lg' : ''
                      }`}
                      style={{
                        backgroundColor: branding.wrapperColor || branding.primaryColor,
                        color: branding.textColor,
                        borderRadius: data.buttonStyle?.shape === 'square' ? '0.25rem' :
                                     data.buttonStyle?.shape === 'soft' ? '0.5rem' :
                                     data.buttonStyle?.shape === 'pill' ? '9999px' : '0.5rem',
                        fontFamily: data.fonts?.body || 'Inter'
                      }}
                    >
                      <Eye className="w-5 h-5" />
                      <span>{data.buttonStyle?.text || 'PDF anzeigen'}</span>
                    </button>
                    
                  </>
                ) : (
                  <div className="p-4 rounded-lg bg-white text-center" style={{ color: textColor }}>
                    <FileText className="w-12 h-12 mx-auto mb-2" style={{ color: textColor, opacity: 0.7 }} />
                    <p className="text-sm" style={{ color: textColor }}>PDF wird hier angezeigt</p>
                  </div>
                )}

                {/* Social Media Links - moved outside of PDF check */}
                {data.socialLinks && Object.keys(data.socialLinks).some(key => data.socialLinks![key]) && (
                  <div className="mt-6 space-y-2">
                    {Object.entries(data.socialLinks).map(([platform, url]) => {
                      if (!url) return null
                      const Icon = socialIcons[platform]
                      if (!Icon) return null
                      
                      // Platform-specific colors
                      const platformColors: Record<string, string> = {
                        instagram: '#E4405F',
                        facebook: '#1877F2',
                        twitter: '#000000',
                        linkedin: '#0A66C2',
                        youtube: '#FF0000',
                        tiktok: '#000000',
                        website: branding.textColor || '#3B82F6'
                      }
                      
                      const platformNames: Record<string, string> = {
                        instagram: 'Instagram',
                        facebook: 'Facebook',
                        twitter: 'X',
                        linkedin: 'LinkedIn',
                        youtube: 'YouTube',
                        tiktok: 'TikTok',
                        website: 'Website'
                      }
                      
                      // Platform-specific official logos
                      const PlatformIcon = (() => {
                        switch (platform) {
                          case 'facebook':
                            return <img src="/images/Facebook_f_logo_(2021).svg.png" alt="Facebook" className="w-7 h-7 object-contain" />
                          case 'instagram':
                            return <img src="/images/Instagram_Glyph_Gradient.png" alt="Instagram" className="w-7 h-7 object-contain" />
                          case 'youtube':
                            return <img src="/images/youtube_logo_nk.png" alt="YouTube" className="w-7 h-7 object-contain" />
                          case 'twitter':
                            return <img src="/images/logo-black.png" alt="X" className="w-7 h-7 object-contain" />
                          case 'linkedin':
                            return <img src="/images/LI-In-Bug.png" alt="LinkedIn" className="w-7 h-7 object-contain" />
                          case 'tiktok':
                            return (
                              <svg className="w-7 h-7" viewBox="0 0 448 512" fill="currentColor">
                                <path d="M448,209.91a210.06,210.06,0,0,1-122.77-39.25V349.38A162.55,162.55,0,1,1,185,188.31V278.2a74.62,74.62,0,1,0,52.23,71.18V0l88,0a121.18,121.18,0,0,0,1.86,22.17h0A122.18,122.18,0,0,0,381,102.39a121.43,121.43,0,0,0,67,20.14Z"/>
                              </svg>
                            )
                          case 'website':
                            return (
                              <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                              </svg>
                            )
                          case 'location':
                            return <Icon className="w-7 h-7" />
                          default:
                            return <Icon className="w-7 h-7" />
                        }
                      })()
                      
                      // Ensure URL has protocol
                      const formattedUrl = url.startsWith('http://') || url.startsWith('https://') 
                        ? url 
                        : `https://${url}`;
                      
                      return (
                        <a
                          key={platform}
                          href={formattedUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all hover:shadow-md hover:translate-x-1"
                          style={{
                            backgroundColor: branding.primaryColor,
                            border: `1px solid ${branding.primaryColor}`
                          }}
                        >
                          <div className="w-9 h-9 flex items-center justify-center flex-shrink-0">
                            {PlatformIcon}
                          </div>
                          <div className="flex-1 text-left">
                            <p className="font-medium" style={{ color: branding.textColor, fontFamily: data.fonts?.body || 'Inter' }}>{platformNames[platform] || platform}</p>
                          </div>
                          <ChevronRight className="w-5 h-5" style={{ color: branding.textColor }} />
                        </a>
                      )
                    })}
                  </div>
                )}

                {/* Company name at bottom */}
                {branding.brandingType !== 'none' && (
                  <div className="text-center mt-8">
                    {branding.brandingType === 'beyondpages' ? (
                      <p className="text-sm opacity-80" style={{ color: branding.textColor, fontFamily: data.fonts?.body || 'Inter' }}>
                        Powered by BeyondPages
                      </p>
                    ) : branding.brandingType === 'custom' && branding.companyName && data.companyPosition === 'bottom' ? (
                      <h2 className={`${data.textSizes?.branding || 'text-lg'} font-semibold`} style={{ color: branding.textColor, fontFamily: data.fonts?.body || 'Inter' }}>
                        {branding.companyName}
                      </h2>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
            </div>
          </div>
        ) : (
          // Structured mode
          <div className="min-h-full flex flex-col relative">
            {/* Header section - 65% */}
            <div 
              className="px-6 relative"
              style={{
                backgroundColor: branding.primaryColor,
                minHeight: '65%',
                paddingTop: '0.5rem',
                paddingBottom: '6rem' // Fixed padding for consistent PDF spacing
              }}
            >
              {/* Content wrapper with dynamic spacing */}
              <div className="h-full flex flex-col">
                {/* Company name at top for custom branding (only when position is top) */}
                {branding.brandingType === 'custom' && branding.companyName && data.companyPosition === 'top' && (
                  <div className="text-center mb-2">
                    <h2 className={`${data.textSizes?.branding || 'text-lg'} font-semibold`} style={{ color: branding.textColor }}>
                      {branding.companyName}
                    </h2>
                  </div>
                )}

                <div className="text-center">
                  {data.title && (
                    <h1 className={`${data.textSizes?.title || 'text-2xl'} font-bold mb-3 break-words`} style={{ 
                      color: branding.textColor,
                      fontFamily: data.fonts?.heading || 'Inter'
                    }}>
                      {data.title}
                    </h1>
                  )}
                  {data.description && (
                    <p className={`${data.textSizes?.description || 'text-base'} opacity-90 break-words whitespace-pre-wrap`} style={{ 
                      color: branding.textColor,
                      fontFamily: data.fonts?.body || 'Inter'
                    }}>
                      {data.description}
                    </p>
                  )}
                </div>
                
                {/* Spacer that pushes content to top and maintains consistent distance to PDF */}
                <div className="flex-1"></div>
              </div>
            </div>

            {/* Body section - 60% */}
            <div 
              className="flex-1 px-6 py-8"
              style={{
                backgroundColor: branding.secondaryColor
              }}
            >
              {/* PDF positioned to overlap both sections */}
              <div className="max-w-sm mx-auto relative" style={{ marginTop: '-7rem' }}>
                {/* Always show PDF preview if URL exists */}
                {data.pdf_url ? (
                  <>
                    <div className="w-full bg-white rounded-lg shadow-lg mb-4 overflow-hidden">
                      <div className="relative bg-gray-50" style={{ paddingBottom: '141.42%' /* A4 ratio 297/210 */ }}>
                        {/* PDF first page preview - non-scrollable */}
                        <div className="absolute inset-0 p-2 overflow-hidden">
                          <div className="w-full h-full relative bg-white rounded">
                            {isPreview ? (
                              // Preview mode - original behavior
                              <iframe
                                src={`${data.pdf_url}#page=1&toolbar=0&navpanes=0&scrollbar=0&statusbar=0&messages=0&scrolling=no&view=FitH`}
                                className="w-full h-full"
                                style={{ 
                                  border: 'none',
                                  pointerEvents: 'none',
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '100%',
                                  overflow: 'hidden'
                                }}
                                scrolling="no"
                                title="PDF Vorschau - Erste Seite"
                              />
                            ) : (
                              // Production mode - use object element
                              <object
                                data={`${data.pdf_url}#page=1&toolbar=0&navpanes=0&view=FitV`}
                                type="application/pdf"
                                className="w-full h-full"
                                style={{ 
                                  border: 'none',
                                  pointerEvents: 'none'
                                }}
                                title="PDF Vorschau - Erste Seite"
                              >
                                <p className="p-4 text-center text-gray-500">PDF Preview</p>
                              </object>
                            )}
                          </div>
                        </div>
                        {/* Overlay to ensure no interaction */}
                        <div className="absolute inset-0" />
                      </div>
                    </div>
                    
                    {/* Always show preview button when PDF exists */}
                    <button
                      onClick={handlePreview}
                      className={`w-full py-3 px-6 font-medium transform transition-transform hover:scale-105 flex items-center justify-center gap-2 ${
                        data.buttonStyle?.shadow ? 'shadow-lg' : ''
                      }`}
                      style={{
                        backgroundColor: branding.wrapperColor || branding.primaryColor,
                        color: branding.textColor,
                        borderRadius: data.buttonStyle?.shape === 'square' ? '0.25rem' :
                                     data.buttonStyle?.shape === 'soft' ? '0.5rem' :
                                     data.buttonStyle?.shape === 'pill' ? '9999px' : '0.5rem',
                        fontFamily: data.fonts?.body || 'Inter'
                      }}
                    >
                      <Eye className="w-5 h-5" />
                      <span>{data.buttonStyle?.text || 'PDF anzeigen'}</span>
                    </button>
                    
                  </>
                ) : (
                  <div className="p-4 rounded-lg bg-white text-center" style={{ color: textColor }}>
                    <FileText className="w-12 h-12 mx-auto mb-2" style={{ color: textColor, opacity: 0.7 }} />
                    <p className="text-sm" style={{ color: textColor }}>PDF wird hier angezeigt</p>
                  </div>
                )}

                {/* Social Media Links - moved outside of PDF check */}
                {data.socialLinks && Object.keys(data.socialLinks).some(key => data.socialLinks![key]) && (
                  <div className="mt-6 space-y-2">
                    {Object.entries(data.socialLinks).map(([platform, url]) => {
                      if (!url) return null
                      const Icon = socialIcons[platform]
                      if (!Icon) return null
                      
                      // Platform-specific colors
                      const platformColors: Record<string, string> = {
                        instagram: '#E4405F',
                        facebook: '#1877F2',
                        twitter: '#000000',
                        linkedin: '#0A66C2',
                        youtube: '#FF0000',
                        tiktok: '#000000',
                        website: branding.primaryColor || '#3B82F6'
                      }
                      
                      const platformNames: Record<string, string> = {
                        instagram: 'Instagram',
                        facebook: 'Facebook',
                        twitter: 'X',
                        linkedin: 'LinkedIn',
                        youtube: 'YouTube',
                        tiktok: 'TikTok',
                        website: 'Website'
                      }
                      
                      // Platform-specific official logos
                      const PlatformIcon = (() => {
                        switch (platform) {
                          case 'facebook':
                            return <img src="/images/Facebook_f_logo_(2021).svg.png" alt="Facebook" className="w-7 h-7 object-contain" />
                          case 'instagram':
                            return <img src="/images/Instagram_Glyph_Gradient.png" alt="Instagram" className="w-7 h-7 object-contain" />
                          case 'youtube':
                            return <img src="/images/youtube_logo_nk.png" alt="YouTube" className="w-7 h-7 object-contain" />
                          case 'twitter':
                            return <img src="/images/logo-black.png" alt="X" className="w-7 h-7 object-contain" />
                          case 'linkedin':
                            return <img src="/images/LI-In-Bug.png" alt="LinkedIn" className="w-7 h-7 object-contain" />
                          case 'tiktok':
                            return (
                              <svg className="w-7 h-7" viewBox="0 0 448 512" fill="currentColor">
                                <path d="M448,209.91a210.06,210.06,0,0,1-122.77-39.25V349.38A162.55,162.55,0,1,1,185,188.31V278.2a74.62,74.62,0,1,0,52.23,71.18V0l88,0a121.18,121.18,0,0,0,1.86,22.17h0A122.18,122.18,0,0,0,381,102.39a121.43,121.43,0,0,0,67,20.14Z"/>
                              </svg>
                            )
                          case 'website':
                            return (
                              <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                              </svg>
                            )
                          case 'location':
                            return <Icon className="w-7 h-7" />
                          default:
                            return <Icon className="w-7 h-7" />
                        }
                      })()
                      
                      // Ensure URL has protocol
                      const formattedUrl = url.startsWith('http://') || url.startsWith('https://') 
                        ? url 
                        : `https://${url}`;
                      
                      return (
                        <a
                          key={platform}
                          href={formattedUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all hover:shadow-md hover:translate-x-1"
                          style={{
                            backgroundColor: branding.primaryColor,
                            border: `1px solid ${branding.primaryColor}`
                          }}
                        >
                          <div className="w-9 h-9 flex items-center justify-center flex-shrink-0">
                            {PlatformIcon}
                          </div>
                          <div className="flex-1 text-left">
                            <p className="font-medium" style={{ color: branding.textColor, fontFamily: data.fonts?.body || 'Inter' }}>{platformNames[platform] || platform}</p>
                          </div>
                          <ChevronRight className="w-5 h-5" style={{ color: branding.textColor }} />
                        </a>
                      )
                    })}
                  </div>
                )}

                {/* Company name at bottom */}
                {branding.brandingType !== 'none' && (
                  <div className="text-center mt-8">
                    {branding.brandingType === 'beyondpages' ? (
                      <p 
                        className="text-sm opacity-80"
                        style={{ 
                          color: textColor,
                          fontFamily: data.fonts?.body || 'Inter'
                        }}
                      >
                        Powered by BeyondPages
                      </p>
                    ) : branding.brandingType === 'custom' && branding.companyName && data.companyPosition === 'bottom' ? (
                      <h2 
                        className={`${data.textSizes?.branding || 'text-lg'} font-semibold`} 
                        style={{ 
                          color: textColor,
                          fontFamily: data.fonts?.body || 'Inter'
                        }}
                      >
                        {branding.companyName}
                      </h2>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* PDF Viewer Modal - only shown in preview mode */}
      {showPDFViewer && data.pdf_url && isPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
          <div className="relative w-full h-full bg-white">
            {/* Close button */}
            <button
              onClick={() => setShowPDFViewer(false)}
              className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
            >
              <X className="w-6 h-6 text-gray-700" />
            </button>
            
            {/* PDF embed */}
            <div className="w-full h-full">
              <iframe
                src={`${data.pdf_url}#toolbar=1&navpanes=0&scrollbar=1`}
                className="w-full h-full"
                style={{ border: 'none' }}
                title="PDF Viewer"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}